// TradeHistoryPage — Recent trades from the AI bot + manual trades
// Layout:
//   - Top nav: < Back | "Trade history"
//   - Filter pills: All / Bot / Manual
//   - Stats row: total trades, win rate, net P&L
//   - Trade list with pair, side, P&L, time

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCardList, TfSectionHeaderView, TfBadge, TfEmpty, TfStatRow } from '../../components/TfPrimitives';

interface Trade {
  id: string;
  pair: string;
  side: 'long' | 'short';
  entry: number;
  exit: number;
  qty: number;
  pnl: number;
  pnlPct: number;
  time: string;
  source: 'bot' | 'manual';
  status: 'closed' | 'open';
  strategy?: string;
}

const DEMO_TRADES: Trade[] = [
  { id: '1', pair: 'NQ',  side: 'short', entry: 18420.00, exit: 18410.50, qty: 1, pnl:  95.00, pnlPct:  0.05, time: '2026-07-24T11:32:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '2', pair: 'ES',  side: 'long',  entry: 5280.25,  exit: 0,        qty: 1, pnl:  85.00, pnlPct:  0.40, time: '2026-07-24T10:15:00Z', source: 'bot',    status: 'open',   strategy: 'Breakout' },
  { id: '3', pair: 'ES',  side: 'long',  entry: 5268.00,  exit: 5275.50,  qty: 1, pnl:  75.00, pnlPct:  0.14, time: '2026-07-24T09:45:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '4', pair: 'NQ',  side: 'short', entry: 18450.00, exit: 18435.00, qty: 1, pnl:  75.00, pnlPct:  0.08, time: '2026-07-24T09:12:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '5', pair: 'CL',  side: 'long',  entry: 78.50,    exit: 78.20,    qty: 1, pnl: -30.00, pnlPct: -0.38, time: '2026-07-24T08:30:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '6', pair: 'GC',  side: 'short', entry: 2380.00,  exit: 2392.00,  qty: 1, pnl: -120.00, pnlPct: -0.50, time: '2026-07-23T15:45:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '7', pair: 'ES',  side: 'long',  entry: 5255.00,  exit: 5280.00,  qty: 1, pnl: 250.00, pnlPct:  0.48, time: '2026-07-23T14:20:00Z', source: 'manual', status: 'closed' },
  { id: '8', pair: 'NQ',  side: 'short', entry: 18500.00, exit: 18420.00, qty: 1, pnl: 400.00, pnlPct:  0.43, time: '2026-07-23T13:10:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '9', pair: 'ES',  side: 'long',  entry: 5260.00,  exit: 5245.00,  qty: 1, pnl: -75.00, pnlPct: -0.29, time: '2026-07-23T11:00:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
  { id: '10', pair: 'NQ', side: 'short', entry: 18410.00, exit: 18380.00, qty: 1, pnl: 150.00, pnlPct:  0.16, time: '2026-07-23T09:30:00Z', source: 'bot',    status: 'closed', strategy: 'Breakout' },
];

const FILTERS = ['All', 'Open', 'Closed', 'Bot', 'Manual'] as const;
type Filter = typeof FILTERS[number];

export function TradeHistoryPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>('All');

  const filtered = DEMO_TRADES.filter((t) => {
    if (filter === 'All') return true;
    if (filter === 'Open' || filter === 'Closed') return t.status === filter.toLowerCase();
    return t.source === filter.toLowerCase();
  });
  const total = filtered.reduce((acc, t) => acc + t.pnl, 0);
  const wins = filtered.filter((t) => t.pnl > 0).length;
  const winRate = filtered.length > 0 ? (wins / filtered.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-tf-bg pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Trade history</div>
        <button className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 4v12M5 12l7 7 7-7M4 20h16" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      {/* Stats row */}
      <div className="mx-4 mt-3">
        <TfStatRow
          stats={[
            { label: 'Trades', value: filtered.length.toString() },
            { label: 'Win rate', value: `${winRate.toFixed(0)}%`, color: '#1C1C1E' },
            { label: 'Net P&L', value: `${total >= 0 ? '+' : ''}$${Math.abs(total).toFixed(2)}`, color: total >= 0 ? '#34C759' : '#FF3B30' },
          ]}
        />
      </div>

      {/* Filter pills */}
      <TfSectionHeaderView title="Trades" />
      <div className="px-4 flex items-center gap-1.5 overflow-x-auto">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`trade-filter-${f.toLowerCase()}`}
            className={['shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition',
              f === filter ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Trade list */}
      <div className="mx-4 mt-3">
        {filtered.length === 0 ? (
          <TfEmpty
            icon="📊"
            title="No trades yet"
            description="Your trade history will appear here once you start trading."
          />
        ) : (
          <TfCardList>
            {filtered.map((t, i) => (
              <button
                key={t.id}
                onClick={() => nav('/portal/trade/' + t.id)}
                data-testid={`trade-${t.id}`}
                className={['flex w-full items-center gap-3 px-4 py-3 text-left active:bg-tf-surface-1 transition', i < filtered.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
              >
                <span
                  className={['flex h-9 w-9 items-center justify-center rounded-full text-[11px] font-bold text-white',
                    t.side === 'long' ? 'bg-tf-profit' : 'bg-tf-loss'
                  ].join(' ')}
                >
                  {t.pair}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-semibold text-tf-text-1">{t.pair} · {t.side === 'long' ? 'LONG' : 'SHORT'}</span>
                    {t.status === 'open' && <TfBadge variant="info">Open</TfBadge>}
                    {t.source === 'bot' && t.strategy && <TfBadge variant="purple">{t.strategy}</TfBadge>}
                  </div>
                  <div className="text-[11px] text-tf-text-3">
                    {t.source === 'bot' ? '🤖 Bot' : '✋ Manual'} · {new Date(t.time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
                <div className="text-right">
                  <div className={['text-[14px] font-semibold tabular-nums', t.pnl >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                    {t.pnl >= 0 ? '+' : ''}${Math.abs(t.pnl).toFixed(2)}
                  </div>
                  <div className={['text-[11px] tabular-nums', t.pnlPct >= 0 ? 'text-tf-profit' : 'text-tf-loss'].join(' ')}>
                    {t.pnlPct >= 0 ? '+' : ''}{t.pnlPct.toFixed(2)}%
                  </div>
                </div>
              </button>
            ))}
          </TfCardList>
        )}
      </div>
    </div>
  );
}
